/* External */
import {
  memo, useState, useEffect, useCallback, useRef,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Toast from '@dtvgo/toast';

/* Theme Assets */
import Logo from '~/assets/theme/svg/brand-logo.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';
import {
  setSelectProfile,
} from '../../../../storage/profiles/actions';
import { cleanOneEntity } from '../../../../storage/utils/actions';
import { useLoading } from '../../../../providers/LoadingProvider';

/* Components */
import FocusableCardProfile from '../../../../components/FocusableCardProfile';
import FocusableCardNewProfile from '../../../../components/FocusableCardNewProfile';
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* Utils */
import { Routes, getLink } from '../../../../utils/router';
import {
  colors,
  TYPE_ACTIONS_PROFILE,
  STORAGE_SHOW_WARNING_DGO_SKYMAIS,
  SHOW_TAPPP_MODAL_STORAGED,
} from '../../../../utils/constants';
import {
  IS_FIRST_WARNING_DGO_SKYMAIS_ACTIVE,
  IS_SECOND_WARNING_DGO_SKYMAIS_ACTIVE,
  IS_TAPPP_SDK_MODAL_ACTIVE,
} from '../../../../utils/env';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* GTM */
import {
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  formatContentData,
} from '../../../../utils/googleTags';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

/* Style */
import './UserProfile.scss';

function UserProfile({ realFocusKey, setFocus }) {
  const [show, setShow] = useState(false);
  const [successText, setSuccessText] = useState('');
  const location = useLocation();
  const history = useHistory();
  const profiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  // const [profiles, setProfiles] = useState(userProfiles);
  const dispatch = useDispatch();
  const userProfileFocusKey = `${realFocusKey}-first-focus-user-profile`;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const tagsMountEditRef = useRef(true);
  const tagsMountCreateRef = useRef(true);
  const tagsMountDeleteRef = useRef(true);
  const { hideLoading, showLoading } = useLoading();
  const isParentalControlChanged = (location.state?.changeParentalControl) || false;

  // When the user press BACK button, prevent default action for back key
  const preventBackKeyAction = useCallback((event) => event.preventDefault(), []);
  useOnBackPress({ listener: preventBackKeyAction });

  const profileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PROFILE.PROFILES_PAGE,
        eventName,
        data: {
          content: {
            ...formatContentData({
              rest: { changeParentalControl: isParentalControlChanged },
            }),
          },
        },
      });
    }
  }, [commonTaggingEvent, isParentalControlChanged]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      timeout = undefined;
      showLoading();
    }, 0);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      } else {
        hideLoading();
      }
    };
  }, [hideLoading, showLoading]);

  useEffect(() => {
    setTimeout(() => {
      hideLoading();
    }, 1000);
    clearTimeout();
  }, [hideLoading]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  useEffect(() => {
    if (IS_FIRST_WARNING_DGO_SKYMAIS_ACTIVE || IS_SECOND_WARNING_DGO_SKYMAIS_ACTIVE) {
      localStorage.setItem(STORAGE_SHOW_WARNING_DGO_SKYMAIS, JSON.stringify(true));
    }

    if (IS_TAPPP_SDK_MODAL_ACTIVE) {
      localStorage.setItem(SHOW_TAPPP_MODAL_STORAGED, JSON.stringify(true));
    }

    localStorage.removeItem('username');
    setFocus(userProfileFocusKey);
  }, [userProfileFocusKey, setFocus]);

  useEffect(() => {
    if (location.state?.create && tagsMountCreateRef.current) {
      if (
        location.state?.typeCreate
        && location.state?.typeCreate === TYPE_ACTIONS_PROFILE.ONBOARDING
      ) {
        profileCommonTagEvent({
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
          eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.PAGE_VIEW_CREATE_SUCCESS_ONBOARDING,
        });
      } else {
        profileCommonTagEvent({
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
          eventName: GTM_EVENT_NAMES.PROFILES_CREATE.PAGE_VIEW_SUCCESS,
        });
      }
      successToast();
      setSuccessText(<FormattedMessage id="profile.create.message" />);
      tagsMountCreateRef.current = false;
    }

    if (location.state?.delete && tagsMountDeleteRef.current) {
      profileCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_DELETE,
        eventName: GTM_EVENT_NAMES.PROFILES_DELETE.PAGE_VIEW_SUCCESS,
      });
      successToast();
      setSuccessText(<FormattedMessage id="profile.delete.message" />);
      tagsMountDeleteRef.current = false;
    }

    if (location.state?.edit && tagsMountEditRef.current) {
      profileCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_EDIT,
        eventName: GTM_EVENT_NAMES.PROFILES_EDIT.PAGE_VIEW_SUCCESS,
      });
      successToast();
      setSuccessText(<FormattedMessage id="profile.edit.message" />);
      tagsMountEditRef.current = false;
    }
  }, [dispatch, location, profileCommonTagEvent]);

  function successToast() {
    setShow(true);
    setTimeout(() => setShow(false), 6000);
  }

  const handleClick = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES.MANAGE_PROFILES });
    history.push(getLink(Routes.Private.Profiles.Management));
  }, [history, profileCommonTagEvent]);

  const selectProfile = useCallback((objProfileAvatar) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES.SELECT_PROFILE });
    const profileWithColor = { ...objProfileAvatar.profile, color: objProfileAvatar.color };
    localStorage.setItem('profile', JSON.stringify(profileWithColor));
    dispatch(setSelectProfile(profileWithColor));
    dispatch(cleanOneEntity('contents'));
    dispatch(cleanOneEntity('seasons'));
    history.push(getLink(Routes.Private.Home));
  }, [history, profileCommonTagEvent, dispatch]);

  const newProfile = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES.CREATE_PROFILE });
    history.push(
      getLink(Routes.Private.Profiles.Create.Name),
      { typeCreateName: TYPE_ACTIONS_PROFILE.CREATE },
    );
  }, [history, profileCommonTagEvent]);

  useEffect(() => {
    if (profiles?.length === 0) {
      history.push(getLink(Routes.Private.Onboarding));
    }
  }, [profiles, history]);

  if (profiles.length <= 0) {
    return (
      <div className="dtv-html5-c-user-profile">
        <div className="dtv-html5-c-user-profile--loading" />
      </div>
    );
  }

  return (
    <div className="dtv-html5-c-user-profile">
      <Toast show={show} text={successText} type="success" />
      <img src={getJsonImage('brand-logo') || Logo} alt="dtv-logo" className="dtv-html5-c-user-profile--logo" />
      <div className="dtv-html5-c-user-profile--title">
        <FormattedMessage id="profile.title" />
      </div>
      <div className="dtv-html5-c-user-profile--cards-profile">
        {profiles?.map((profile, index) => (
          <FocusableCardProfile
            key={profile?.id}
            name={profile?.name}
            color={colors[index + 1]}
            focusKey={index === 0 ? userProfileFocusKey : ''}
            onEnterPress={() => selectProfile({ profile, color: colors[index + 1], focusKey: index === 0 ? userProfileFocusKey : '' })}
          />
        ))}
        {profiles?.length < 5 ? (
          <FocusableCardNewProfile
            text={<FormattedMessage id="profile.create" />}
            onEnterPress={newProfile}
          />
        ) : ''}
      </div>
      <FocusableButtonprofile
        onEnterPress={handleClick}
        className="dtv-btn-tv dtv-html5-c-user-profile--button"
        type="submit"
        secondGray
        bordered
      >
        <FormattedMessage id="profile.manage" />
      </FocusableButtonprofile>
    </div>
  );
}

UserProfile.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(UserProfile)));
